import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { createModelOnlyfansApiAccount, getApiErrorMessage, getModels } from '../../api'
import { EmptyState, LoadingBlock, MetricCard, Section, StatusPill } from '../../components/v2/V2Primitives'
import { useWorkspaceScope } from '../../contexts/WorkspaceScopeContext'
import { isManagerRole, getStoredAuthRole } from '../../lib/realm'
import CreateModelWorkspace from '../../components/CreateModelWorkspace'
import { asList, hasConnectedProvider, modelTitle, providerLabel, providerLinks, providerStatus, setupChecklist, statusTone } from './v2Utils'

export default function AdminV2Models() {
  const navigate = useNavigate()
  const { loading: scopeLoading, fanvueReviewWorkspace, refreshModels, setSelectedModelId, capabilities } = useWorkspaceScope()
  const canManage = isManagerRole() && capabilities?.is_agency_member !== true
  const canOpenContent = capabilities?.is_agency_member && capabilities?.agency_permissions?.includes('content')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [models, setModels] = useState([])
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [createBusy, setCreateBusy] = useState(false)
  const [createStatus, setCreateStatus] = useState('')
  const [createDraft, setCreateDraft] = useState({
    apiKey: '',
    accountId: '',
    connectionId: '',
    email: '',
    accountDescription: '',
    timezone: '',
    isTest: false,
  })

  useEffect(() => {
    let alive = true
    getModels()
      .then((result) => { if (alive) setModels(asList(result)) })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load models')) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return models.filter((model) => {
      const title = modelTitle(model).toLowerCase()
      const checklist = setupChecklist(model)
      const connected = hasConnectedProvider(model)
      if (q && !title.includes(q)) return false
      if (filter === 'attention') return !connected || checklist.some((item) => !item.done)
      if (filter === 'connected') return connected
      if (filter === 'test') return Boolean(model.is_test)
      return true
    })
  }, [filter, models, query])

  function updateCreateDraft(patch) {
    setCreateDraft((current) => ({ ...current, ...patch }))
  }

  async function createOnlyFansApiModel(event) {
    event.preventDefault()
    if (!createDraft.accountId.trim()) return
    setCreateBusy(true)
    setCreateStatus('')
    setError('')
    try {
      const created = await createModelOnlyfansApiAccount({
        apiKey: createDraft.apiKey.trim() || undefined,
        accountId: createDraft.accountId.trim(),
        connectionId: createDraft.connectionId.trim() || undefined,
        email: createDraft.email.trim() || undefined,
        accountDescription: createDraft.accountDescription.trim() || undefined,
        timezone: createDraft.timezone.trim() || undefined,
        isTest: Boolean(createDraft.isTest),
      })
      if (created?.id) setModels((current) => [created, ...current.filter((model) => model.id !== created.id)])
      setCreateDraft({
        apiKey: '',
        accountId: '',
        connectionId: '',
        email: '',
        accountDescription: '',
        timezone: '',
        isTest: false,
      })
      setCreateStatus('OnlyFansAPI model created and connected.')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create OnlyFansAPI model'))
    } finally {
      setCreateBusy(false)
    }
  }

  if (loading) return <LoadingBlock label="Loading model portfolio..." />

  return (
    <div className="v2-stack">
      <div className="v2-metrics-grid">
        <MetricCard label="All models" value={models.length} detail="Across all providers" />
        <MetricCard label="Connected" value={models.filter(hasConnectedProvider).length} detail="At least one active account" tone="good" />
        <MetricCard label="Need attention" value={models.filter((m) => setupChecklist(m).some((item) => !item.done)).length} detail="Setup or content missing" tone="warn" />
        <MetricCard label="Test models" value={models.filter((m) => m.is_test).length} detail="QA-safe workspace" />
      </div>

      <Section
        eyebrow="Model operating system"
        title="Models"
        description="Each model has setup, connections, content, inbox and automation context in one workspace."
        actions={
          <div className="v2-toolbar">
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search models" />
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="attention">Needs attention</option>
              <option value="connected">Connected</option>
              <option value="test">Test</option>
            </select>
          </div>
        }
      >
        {error ? <div className="v2-error">{error}</div> : null}
        {createStatus ? <div className="v2-success">{createStatus}</div> : null}
        {['agency', 'admin', 'superadmin'].includes(getStoredAuthRole()) && !scopeLoading && !fanvueReviewWorkspace ? <CreateModelWorkspace onCreated={async (model) => {
          await refreshModels()
          setSelectedModelId(String(model.id))
          navigate(`/v2/onboarding?model_id=${model.id}`)
        }} /> : null}
        {canManage && !scopeLoading && !fanvueReviewWorkspace ? <details className="v2-config-card">
          <summary>Optional shortcut: connect an existing OnlyFansAPI account</summary>
          <div className="v2-card-heading">
            <div>
              <p className="v2-eyebrow">New model</p>
              <h3>Add existing OnlyFansAPI account</h3>
            </div>
            <StatusPill tone="good">active</StatusPill>
          </div>
          <form className="v2-form-grid" onSubmit={createOnlyFansApiModel}>
            <label className="v2-field">
              <span>Account ID</span>
              <input value={createDraft.accountId} onChange={(event) => updateCreateDraft({ accountId: event.target.value })} placeholder="OnlyFansAPI account id" />
            </label>
            <label className="v2-field">
              <span>API key</span>
              <input type="password" value={createDraft.apiKey} onChange={(event) => updateCreateDraft({ apiKey: event.target.value })} placeholder="Optional if global key is configured" />
            </label>
            <label className="v2-field">
              <span>Connection ID</span>
              <input value={createDraft.connectionId} onChange={(event) => updateCreateDraft({ connectionId: event.target.value })} placeholder="Optional override" />
            </label>
            <label className="v2-field">
              <span>Email label</span>
              <input value={createDraft.email} onChange={(event) => updateCreateDraft({ email: event.target.value })} placeholder="creator@example.com" />
            </label>
            <label className="v2-field">
              <span>Timezone</span>
              <input value={createDraft.timezone} onChange={(event) => updateCreateDraft({ timezone: event.target.value })} placeholder="America/New_York" />
            </label>
            <label className="v2-check-field">
              <input type="checkbox" checked={createDraft.isTest} onChange={(event) => updateCreateDraft({ isTest: event.target.checked })} />
              <span>Test model</span>
            </label>
            <label className="v2-field v2-field-wide">
              <span>Model profile</span>
              <textarea value={createDraft.accountDescription} onChange={(event) => updateCreateDraft({ accountDescription: event.target.value })} rows={4} placeholder="Persona, tone, boundaries, sales style and important context." />
            </label>
            <button type="submit" className="v2-button v2-button-secondary" disabled={!createDraft.accountId.trim() || createBusy}>
              {createBusy ? 'Creating...' : 'Create and connect model'}
            </button>
          </form>
        </details> : null}
        {filtered.length ? (
          <div className="v2-model-grid">
            {filtered.map((model) => <ModelCard key={model.id} model={model} canManage={canManage} canOpenContent={canOpenContent} />)}
          </div>
        ) : (
          <EmptyState title="No models match this view" description="Try another filter or search query." />
        )}
      </Section>
    </div>
  )
}

function ModelCard({ model, canManage, canOpenContent }) {
  const links = providerLinks(model)
  const checklist = setupChecklist(model)
  const done = checklist.filter((item) => item.done).length
  const ready = done === checklist.length

  return (
    <article className="v2-model-card">
      <div className="v2-model-card-top">
        <div className="v2-avatar">{modelTitle(model).slice(0, 1).toUpperCase()}</div>
        <div>
          <h3>{modelTitle(model)}</h3>
          <p>Model #{model.id}{model.is_test ? ' · test' : ''}</p>
        </div>
        <StatusPill tone={ready ? 'good' : 'warn'}>{ready ? 'Ready' : `${done}/${checklist.length}`}</StatusPill>
      </div>

      <div className="v2-provider-strip">
        {links.length ? links.slice(0, 4).map((link) => (
          <span key={link.id || `${link.provider}-${link.external_id || ''}`} className={`v2-provider-chip v2-provider-${statusTone(providerStatus(link))}`}>
            {providerLabel(link.provider)} · {providerStatus(link)}
          </span>
        )) : <span className="v2-provider-chip v2-provider-bad">No provider connected</span>}
      </div>

      <ul className="v2-checklist-mini">
        {checklist.map((item) => (
          <li key={item.key} className={item.done ? 'is-done' : ''}>
            <span>{item.done ? 'OK' : 'TODO'}</span>
            {item.label}
          </li>
        ))}
      </ul>

      {canManage || canOpenContent ? <div className="v2-card-actions">
        <Link to={`/v2/models/${model.id}${canOpenContent ? '?tab=Content' : ''}`} className="v2-btn v2-btn-primary">{canOpenContent ? 'Open content' : 'Open workspace'}</Link>
      </div> : null}
    </article>
  )
}
